import { Edit } from '@mui/icons-material';
import { BookingOrigin } from '@utriper/core';
import { FORM_IDS } from 'modules/Pricing/PriceGroupId/styles';

const cellStyle = { justifyContent: 'flex-start', px: 2, fontSize: '14px', fontWeight: 400 };
const headerCellStyle = { justifyContent: 'flex-start', fontSize: '14px', fontWeight: 'bold', backgroundColor: '#F2F0ED !important' };
const headerGroupRow = {
    rowId: 'headerGroup',
    cells: [],
    height: 45,
};
const hiddenCell = { type: 'header', text: '', style: { display: 'none' } };

export const getColumns = (hideDefaultOrigin, units = [], bookingOrigins = [], translateOptions = { translate: () => {}, language: 'es' }) => {
    const columns = [
        {
            columnId: translateOptions?.translate('quantity_columnn'), // RANGES COLUMN
            width: 210,
        },
    ];

    if (Array.isArray(units) && units?.length > 0) {
        for (const unit of units) {
            if (!hideDefaultOrigin) {
                columns.push({
                    columnId: `${unit.id}-all`, // ALL ORIGINS (DEFAULT)
                    width: 190,
                    name: translateOptions?.translate('default_origin_column'),
                    unitId: unit.id,
                    bookingOriginId: null,
                });
            }

            for (const bookingOriginId of bookingOrigins ?? []) {
                columns.push({
                    columnId: `${unit.id}-${bookingOriginId}`,
                    width: 190,
                    name: BookingOrigin.getTranslatedName(BookingOrigin.types, bookingOriginId, translateOptions.language)?.[0] ?? '',
                    unitId: unit.id,
                    bookingOriginId,
                });
            }
        }
    }

    return columns;
};

export const getRows = (values, headerType, canEdit, hideDefaultOrigin, handleOpenEdit = () => {}, units = [], bookingOrigins = [], translateOptions = { translate: () => {}, language: 'es' }) => [
    ...getHeaderRow(headerType, canEdit, hideDefaultOrigin, handleOpenEdit, units, bookingOrigins, translateOptions),
    ...values?.map(({ quantityFrom, quantityTo, prices }, index) => ({
        rowId: index,
        quantityFrom,
        quantityTo: Number.isInteger(quantityTo) ? quantityTo : quantityFrom,
        cells: [
            {
                type: 'text',
                text: getQuantityLabel(quantityFrom, quantityTo, translateOptions?.translate),
                style: cellStyle,
                nonEditable: true,
            },
            ...prices?.map(({ price = null, unitId, bookingOriginId }) => ({
                type: 'number',
                value: !Number.isNaN(price) ? price : null,
                unitId,
                bookingOriginId: bookingOriginId ?? null,
                nonEditable: false,
            })),
        ],
    })),
];

export const getQuantityLabel = (quantityFrom, quantityTo, translate = () => {}) => {
    if (quantityFrom === quantityTo) {
        return String(quantityFrom);
    }

    if (!Number.isInteger(quantityTo)) {
        return `${quantityFrom}+`;
    }

    if (Number.isInteger(quantityTo) && quantityFrom !== quantityTo) {
        return `${quantityFrom} ${translate('to')} ${quantityTo}`;
    }

    return '';
};

export const getHeaderRow = (headerType, canEdit, hideDefaultOrigin, handleOpenEdit = () => {}, units = [], bookingOrigins = [], translateOptions = { translate: () => {}, language: 'es' }) => {
    const rangeCell = {
        type: headerType,
        text: translateOptions?.translate('quantity_column'),
        style: headerCellStyle,
        icon: canEdit ? <Edit fontSize="small" /> : null,
        onClick: canEdit ? (cell) => handleOpenEdit(FORM_IDS.EDIT_RANGES, cell) : null,
        sx: { p: 1 },
        rowspan: 2,
        colspan: 1,
    };

    if (!Array.isArray(units) || units?.length <= 0) {
        return [headerGroupRow];
    }

    const unitsCells = getUnitsCells(units, bookingOrigins, headerType, canEdit, hideDefaultOrigin, handleOpenEdit);
    const originsCells = getOriginsCells(hideDefaultOrigin, bookingOrigins, units, translateOptions);

    const headerRow = [
        { ...headerGroupRow, cells: [rangeCell, ...unitsCells] },
        {
            rowId: 'header',
            cells: [hiddenCell, ...originsCells],
        },
    ];

    return headerRow;
};

const getUnitsCells = (units = [], bookingOrigins = [], headerType, canEdit, hideDefaultOrigin, handleOpenEdit = () => {}) => {
    let unitsCells = [];
    const colspan = hideDefaultOrigin ? bookingOrigins?.length ?? 0 : bookingOrigins?.length + 1 ?? 1;

    if (Array.isArray(units) && units?.length > 0) {
        const placeholderRows = getPlaceholderRows(bookingOrigins, hideDefaultOrigin);

        for (const unit of units) {
            const unitCell = {
                type: headerType,
                text: unit?.name,
                unitId: canEdit ? unit?.id : null,
                style: headerCellStyle,
                icon: canEdit ? <Edit fontSize="small" /> : null,
                onClick: canEdit ? (cell) => handleOpenEdit(FORM_IDS.EDIT_UNIT, cell) : null,
                sx: { p: 1 },
                colspan,
                rowspan: 1,
            };

            unitsCells = unitsCells.concat([unitCell, ...placeholderRows]);
        }
    }

    return unitsCells;
};

const getPlaceholderRows = (bookingOrigins = [], hideDefaultOrigin) => {
    if (!hideDefaultOrigin || (hideDefaultOrigin && bookingOrigins?.length > 1)) {
        return bookingOrigins?.map((_) => hiddenCell);
    }

    return [];
};

const getOriginsCells = (hideDefaultOrigin, bookingOrigins = [], units = [], translateOptions = { translate: () => {}, language: 'es' }) => {
    let originsCells = [];
    if (!hideDefaultOrigin) {
        originsCells.push({
            type: 'text',
            text: translateOptions?.translate('default_origin_column'),
            style: headerCellStyle,
            className: 'rg-header-text-cell',
            nonEditable: true,
        });
    }

    if (Array.isArray(bookingOrigins) && bookingOrigins?.length > 0) {
        const bookingOriginCells = bookingOrigins.map((originId) => ({
            type: 'text',
            text: BookingOrigin.getTranslatedName(BookingOrigin.types, originId, translateOptions?.language)?.[0] ?? '',
            style: headerCellStyle,
            className: 'rg-header-text-cell',
            nonEditable: true,
        }));
        originsCells = originsCells.concat(bookingOriginCells);
    }

    // For each unit there must be a batch of origins
    if (Array.isArray(units) && units?.length > 0) {
        originsCells = units?.flatMap((_) => originsCells);
    }

    return originsCells;
};
